import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Switch,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AppIconButton } from '../../../components/design-system';
import { useAppToolbar } from '../../../contexts/AppToolbarContext';
import { useProviderSettings } from '../../../contexts/ProviderSettingsContext';
import { EnhancedTable, type TableColumn } from '../../../components/ui/EnhancedTable';

/**
 * Feature Flags Component
 * Tab 8: Manage global and per-tenant feature toggles
 */
export default function FeatureFlags() {
  const {
    featureFlags,
    flagsLoading,
    fetchFeatureFlags,
    toggleFeatureFlag,
    initializeDefaultFlags,
  } = useProviderSettings();

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState<Record<string, unknown>>({});

  // Load flags on mount
  useEffect(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  const handleToggleFlag = async (flagKey: string, currentState: boolean) => {
    setUpdating(flagKey);
    setErrorMessage('');

    try {
      await toggleFeatureFlag(flagKey, !currentState);
      setSuccessMessage(
        `Feature flag '${flagKey}' ${!currentState ? 'enabled' : 'disabled'} successfully`
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to toggle feature flag'
      );
    } finally {
      setUpdating(null);
    }
  };

  const handleInitializeDefaults = async () => {
    setErrorMessage('');
    try {
      await initializeDefaultFlags();
      setSuccessMessage('Default feature flags initialized successfully');
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to initialize flags'
      );
    }
  };

  const handleRefresh = useCallback(async () => {
    setErrorMessage('');
    try {
      await fetchFeatureFlags();
      setSuccessMessage('Feature flags refreshed');
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to refresh flags'
      );
    }
  }, [fetchFeatureFlags]);

  const wellKnownFlags = [
    'maintenance_mode',
    'cci_operations',
    'cci_vision',
    'spatial_connect',
    'spatial_uwb',
    'axon_gateways',
  ];

  // Filter feature flags
  const filteredFlags = featureFlags.filter((flag) => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        flag.flagKey.toLowerCase().includes(search) ||
        flag.flagName.toLowerCase().includes(search) ||
        (flag.description && flag.description.toLowerCase().includes(search));
      if (!matchesSearch) return false;
    }

    // Status filter
    if (filters.status) {
      if (filters.status === 'enabled' && !flag.isEnabled) return false;
      if (filters.status === 'disabled' && flag.isEnabled) return false;
    }

    return true;
  });

  // Define table columns
  const columns: TableColumn<typeof featureFlags[0]>[] = [
    {
      field: 'flagKey',
      headerName: 'Flag Key',
      flex: 1.5,
      minWidth: 180,
      sortable: true,
      renderCell: (row) => (
        <Typography variant="body2" fontFamily="monospace">
          {row.flagKey}
        </Typography>
      ),
    },
    {
      field: 'flagName',
      headerName: 'Name',
      flex: 1.5,
      minWidth: 150,
      sortable: true,
      renderCell: (row) => (
        <Typography variant="body2" fontWeight="medium">
          {row.flagName}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 250,
      sortable: false,
      renderCell: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.description || '—'}
        </Typography>
      ),
    },
    {
      field: 'isEnabled',
      headerName: 'Status',
      width: 120,
      align: 'center',
      sortable: true,
      filterable: true,
      valueGetter: (row) => (row.isEnabled ? 1 : 0),
      renderCell: (row) => (
        <Chip
          label={row.isEnabled ? 'Enabled' : 'Disabled'}
          color={row.isEnabled ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'toggle',
      headerName: 'Global Toggle',
      width: 150,
      align: 'center',
      sortable: false,
      renderCell: (row) => (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Switch
            checked={row.isEnabled}
            onChange={() => handleToggleFlag(row.flagKey, row.isEnabled)}
            disabled={updating === row.flagKey}
            color="primary"
          />
          {updating === row.flagKey && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Box>
      ),
    },
  ];

  // Export handler
  const handleExport = () => {
    const csvHeaders = ['Flag Key', 'Name', 'Description', 'Status'];
    const csvRows = filteredFlags.map((flag) => [
      flag.flagKey,
      `"${flag.flagName}"`,
      `"${flag.description || ''}"`,
      flag.isEnabled ? 'Enabled' : 'Disabled'
    ]);

    const csvContent = [csvHeaders.join(','), ...csvRows.map((row) => row.join(','))].join(
      '\n'
    );

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `feature-flags-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void handleRefresh()} disabled={flagsLoading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [handleRefresh, flagsLoading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 2,
      }}
    >
      {featureFlags.length === 0 && !flagsLoading ? (
        <Alert severity="info" sx={{ flexShrink: 0, mb: 0 }}>
          No feature flags found. Click &ldquo;Initialize Defaults&rdquo; in the table toolbar to create the core feature flags.
        </Alert>
      ) : null}

      <EnhancedTable
        data={filteredFlags}
        totalCount={filteredFlags.length}
        loading={flagsLoading}
        columns={columns}
        getRowId={(row) => row.flagKey}
        page={page + 1}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageChange={(newPage) => setPage(newPage - 1)}
        onPageSizeChange={setPageSize}
        searchable
        searchTerm={searchTerm}
        searchPlaceholder="Search by flag key, name, or description..."
        onSearchChange={setSearchTerm}
        filters={filters}
        filterOptions={[
          {
            field: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { label: 'Enabled', value: 'enabled' },
              { label: 'Disabled', value: 'disabled' },
            ],
          },
        ]}
        onFilterChange={setFilters}
        exportable
        onExport={handleExport}
        emptyStateTitle="No Feature Flags Found"
        emptyStateDescription="Click 'Initialize Defaults' to create the core feature flags."
        toolbarActions={[
          {
            label: 'Initialize Defaults',
            icon: <AddIcon />,
            onClick: handleInitializeDefaults,
            variant: 'outlined',
          },
        ]}
        rowHeight={56}
        stickyHeader
        maxHeight="100%"
      />

      <Card sx={{ mt: 2, flexShrink: 0 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Well-Known Feature Flags
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            The system supports the following standard feature flags:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {wellKnownFlags.map((flag) => (
              <Chip
                key={flag}
                label={flag}
                size="small"
                variant="outlined"
                color={
                  featureFlags.find((f) => f.flagKey === flag)?.isEnabled
                    ? 'success'
                    : 'default'
                }
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
